<script>
  import { createEventDispatcher } from "svelte";
  import isEqual from "lodash/isEqual";

  import { draggable, clickOutside } from "svelte-utilities";

  import { discontiguousKey } from "#src/extensions/event-keys.js";

  const dispatch = createEventDispatcher();

  export let classes = "";
  export let selection = [];
  export let ignoreClick = [];

  let id = crypto.randomUUID();
  let table;
  let currentCell = null;
  let clickingTable = false;
  let left = false;

  $: selected = updateSelected(selection);

  function updateSelected(selection) {
    const sel = {};
    selection.forEach((s) => {
      for (let row = s[0]; row <= s[2]; row++) {
        for (let col = s[1]; col <= s[3]; col++) {
          sel[`${row}.${col}`] = true;
        }
      }
    });

    return sel;
  }

  function getEventTd(e) {
    try {
      const tdc = e.target.closest("td");
      const thc = e.target.closest("th");
      const td = tdc || thc;
      if (!td) return null;
      if (!table.contains(td)) return null;

      const row = parseInt(td.getAttribute("data-row"));
      const col = parseInt(td.getAttribute("data-column"));

      if (isNaN(row) || isNaN(col)) return null;

      return {
        el: td,
        row,
        col,
      };
    } catch {
      return null;
    }
  }

  function select(td) {
    currentCell = { row: td.row, col: td.col };
    selection = [[td.row, td.col, td.row, td.col]];
  }

  function dragSelect(td) {
    if (currentCell) {
      const minR = Math.min(currentCell.row, td.row);
      const minC = Math.min(currentCell.col, td.col);
      const maxR = Math.max(currentCell.row, td.row);
      const maxC = Math.max(currentCell.col, td.col);
      selection[selection.length - 1] = [minR, minC, maxR, maxC];
    } else {
      select(td);
    }
  }

  function metaSelect(td) {
    currentCell = { row: td.row, col: td.col };
    const n = [td.row, td.col, td.row, td.col];

    if (selection.some((s) => isEqual(s, n))) {
      const index = selection.findIndex((s) => isEqual(s, n));
      selection.splice(index, 1);
    }

    selection.push(n);
  }

  function dragstart(e) {
    clickingTable = true;
    table.focus({ preventScroll: true });
    const td = getEventTd(e.detail.event);
    if (!td) return;

    if (e.detail.event.shiftKey) {
      dragSelect(td);
    } else if (discontiguousKey(e.detail.event)) {
      metaSelect(td);
    } else {
      select(td);
    }
  }

  function drag(e) {
    const td = getEventTd(e.detail.event);
    if (!td) return;

    dragSelect(td);
  }

  function dragend(e) {
    clickingTable = false;
    const td = getEventTd(e.detail.event);
    if (!td) return;

    dragSelect(td);
  }

  function deselect() {
    currentCell = null;
    selection = [];
  }

  function copy(e) {
    // Ignoring all but the last selection
    // TODO: support multiple selections
    const sel = selection[selection.length - 1];
    if (!sel) return;

    dispatch("copy", { selection: sel });
  }

  function keydown(e) {
    if (e.key === "c" && discontiguousKey(e)) {
      copy(e);
    }
  }

  function mouseleave(e) {
    if (clickingTable) {
      left = true;
    }
  }

  function mouseenter(e) {
    left = false;
  }

  function outclick(e) {
    if (ignoreClick.some((el) => el?.contains(e.clickTarget))) {
      return;
    }

    if (!left) {
      deselect();
    }
    left = false;
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<table
  id={`copyable-table-${id}`}
  class={`copyable-table ${classes}`}
  bind:this={table}
  tabindex="0"
  use:draggable
  use:clickOutside={[table, ...ignoreClick]}
  on:keydown={keydown}
  on:dragstart={dragstart}
  on:drag={drag}
  on:dragend={dragend}
  on:outclick={outclick}
  on:mouseleave={mouseleave}
  on:mouseenter={mouseenter}>
  <slot {selected} />
</table>
